import { AxiosProgressEvent, AxiosRequestConfig, AxiosResponse } from 'axios';
import { JurisdictionsGetData } from '~/api/def/endpoints/jurisdictionEndpoint';
import {
  BqResponse,
  ResponseOtherError,
  ResponseValidationError,
} from '~/api/def/endpoints/types';
import { defaultServer } from '~/api/def/servers/defaultServer';
import {
  DashboardData,
  FundSeries,
  FundSingletons,
  PortfolioAsset,
} from '~/components/pages/analyticsDashboard/chart/chartConfigMapSample';
import { getExecEndpoint } from '../../service/getExecEndpoint';
import { PortfolioCompanyGetResponse } from './portfolioCompanyEndpoint';

export const fundEndpoint = {
  server: defaultServer,
  path: 'fund',
  description: 'Fund CRU (no D) operations and fund documents GET / PATCH',
};

const endpointFundExec = getExecEndpoint<FundEndpointDef>(fundEndpoint);

export const endpointFundGET = async (
  includeSyncFolders: boolean = false,
  includeAnalytics: boolean = false,
) => {
  let urlExtra = '';
  if (includeSyncFolders) {
    urlExtra += '?includeSyncFolders=true';
  }
  if (includeAnalytics) {
    urlExtra += (urlExtra ? '&' : '?') + 'includeAnalytics=true';
  }
  return endpointFundExec<'GET'>({
    method: 'GET',
    urlExtra: urlExtra || undefined,
  });
};

export const endpointFundPOST = async (payload: Omit<FundPostPayload, 'id'>) =>
  endpointFundExec<'POST'>({ method: 'POST', payload: payload });

export const endpointFundPATCH = async (
  id: string,
  payload: FundPatchPayload,
) => {
  return await endpointFundExec<'PATCH'>({
    method: 'PATCH',
    payload: payload,
    urlExtra: `/${id}`,
  });
};

const endpointFundDocumentsExec =
  getExecEndpoint<FundDocumentsEndpointDef>(fundEndpoint);

export const endpointFundDocumentsPOST = async (
  id: string,
  payload: FundDocumentsPatchPayloadArgument,
  portfolioCompanyId?: string,
  onProgress?: (progressEvent: AxiosProgressEvent) => void,
) => {
  const formData = new FormData();
  payload.documents.forEach((file) => {
    formData.append('documents', file);
  });

  if (portfolioCompanyId) {
    formData.append('portfolioCompanyId', portfolioCompanyId);
  }
  const config: AxiosRequestConfig = {
    headers: { 'Content-Type': 'multipart/form-data' },
  };
  if (onProgress) {
    config['onUploadProgress'] = onProgress;
  }

  return await endpointFundDocumentsExec<'POST'>({
    method: 'POST',
    payload: formData,
    config: config,
    urlExtra: `/${id}/document`,
    returnAxiosResponse: true,
  });
};

export const endpointFundDocumentsGET = async (
  id: string,
  portfolioCompanyId?: string,
) => {
  return await endpointFundDocumentsExec<'GET'>({
    method: 'GET',
    urlExtra: `/${id}/document${portfolioCompanyId ? `?portfolioCompanyId=${portfolioCompanyId}` : ''}`,
  });
};

const endpointFundDocumentExec =
  getExecEndpoint<FundDocumentEndpointDef>(fundEndpoint);
export interface FundDocumentsEndpointDef {
  endpoint: typeof fundEndpoint;
  methods: {
    GET: {
      response: FundGetDocumentResponse;
    };
    POST: {
      response: AxiosResponse<FundPostResponse>;
      payload: FundDocumentsPatchPayload; //do send id here?
    };
  };
}
interface FundDocumentEndpointDef {
  endpoint: typeof fundEndpoint;
  methods: {
    PATCH: {
      response: FundPostResponse;
      payload: FundDocumentPatchPayloadArgument;
    };
  };
}
export const endpointFundDocumentPATCH = async (
  fundId: string,
  docId: string,
  payload: FundDocumentPatchPayloadArgument,
) => {
  return await endpointFundDocumentExec<'PATCH'>({
    method: 'PATCH',
    payload: payload,
    urlExtra: `/${fundId}/document/${docId}`,
  });
};

const endpointFundPortfolioExec =
  getExecEndpoint<FundPortfolioEndpointDef>(fundEndpoint);

export const endpointFundPortfolioGET = async (id: string) => {
  return await endpointFundPortfolioExec<'GET'>({
    method: 'GET',
    urlExtra: `/${id}/portfolio`,
  });
};

const endpointFundGDriveChannelSyncExec =
  getExecEndpoint<FundGooglWatchChannelEndpointDef>(fundEndpoint);

export const endpointFundGooglWatchChannelPOST = async (
  payload: FundGoogleWatchAddSyncPayload,
) => {
  return await endpointFundGDriveChannelSyncExec<'POST'>({
    method: 'POST',
    payload: payload,
    urlExtra: `/g-drive-sync`,
  });
};

export const endpointFundGooglWatchChannelDELETE = async (
  payload: FundGoogleWatchDeleteSyncPayload,
) => {
  return await endpointFundGDriveChannelSyncExec<'DELETE'>({
    method: 'DELETE',
    urlExtra: `/g-drive-sync/${payload.channelId}`,
  });
};

const endpointFundPcGDriveChannelSyncExec =
  getExecEndpoint<FundPcGooglWatchChannelEndpointDef>(fundEndpoint);

export const endpointFundPcGooglWatchChannelSyncPUT = async (
  fundId: string,
  payload: FundGoogleWatchAutoSyncPayload,
) => {
  return await endpointFundPcGDriveChannelSyncExec<'PUT'>({
    method: 'PUT',
    payload: payload,
    urlExtra: `/${fundId}/pc-g-drive-sync`,
  });
};

export interface FundEndpointDef {
  endpoint: typeof fundEndpoint;
  methods: {
    GET: {
      response: FundGetDataResponse;
    };
    POST: {
      response: FundPostResponse;
      payload: Omit<FundPostPayload, 'id'>;
    };
    PATCH: {
      response: FundPostResponse;
      payload: FundPatchPayload;
    };
  };
}

export interface FundGooglWatchChannelEndpointDef {
  endpoint: typeof fundEndpoint;
  methods: {
    POST: {
      response: FundGoogleWatchAddSyncResponse;
      payload: FundGoogleWatchAddSyncPayload;
    };
    DELETE: {
      response: FundGoogleWatchAddSyncResponse;
      payload: FundGoogleWatchDeleteSyncPayload;
    };
  };
}

export interface FundPcGooglWatchChannelEndpointDef {
  endpoint: typeof fundEndpoint;
  methods: {
    PUT: {
      response: FundGoogleWatchAutoSyncResponse;
      payload: FundGoogleWatchAutoSyncPayload;
    };
  };
}

interface FundPortfolioEndpointDef {
  endpoint: typeof fundEndpoint;
  methods: {
    GET: {
      response: PortfolioCompanyGetResponse;
    };
  };
}

export type FundGetData = {
  id: string;
  name: string;
  inceptionDate: Date | string;
  terminationDate: Date | string;
  size: number;
  currency: string;
  jurisdiction: JurisdictionsGetData;
  googleWatchChannels?: GoogleWatchChannel[];
  currentNav?: number;
  assetDeployed?: number;
  portfolioCompanyCount?: number;
  investmentFocus?: string;
  status?: string;
  irr?: number;
  writeOff?: number;
  exit?: number;
  companySetupCost?: number;
  investmentCostRate?: number;
  managementFees?: number;
  carrySetupThresholds?: carrySetupThresholdsType[];
  fundValuationEvents?: FundValuationEvent[];
  pcGDriveFolderSync: boolean;
};

type FundValuationEvent = {
  id: string;
  currency: string;
  valuationDate: Date | string;
  valueLocal: number;
};

export type GoogleWatchChannel = {
  id: string;
  folderName: string;
};

export type FundGetDataResponse =
  | FundGetDataResponseOk
  | ResponseValidationError
  | ResponseOtherError
  | undefined;

export type FundGetDataResponseOk = {
  success: 1;
  response: 200;
  message: string;
  data: FundGetData[];
};

export type FundDocumentsPatchPayloadArgument = {
  documents: File[];
};

export type FundDocumentsPatchPayload = FormData;

export type FundPostPayload = Omit<FundGetData, 'jurisdiction'> & {
  jurisdictionId: string;
};

export type FundPatchPayload = {
  jurisdictionId?: string;
  name?: string;
  inceptionDate?: Date | string;
  terminationDate?: Date | string;
  size?: number;
  currency?: string;
  googleWatchChannels?: GoogleWatchChannel[];
  currentNav?: number;
  assetDeployed?: number;
  portfolioCompanyCount?: number;
  investmentFocus?: string;
  status?: string;
  irr?: number;
  writeOff?: number;
  exit?: number;
  companySetupCost?: number | null;
  investmentCostRate?: number | null;
  managementFees?: number | null;
  carrySetupThresholds?: carrySetupThresholdsType[];
};

export type carrySetupThresholdsType = {
  condition: '<' | 'default';
  value: number | null;
  percentage: number;
};

export type FundPostResponse = BqResponse<{ data: { id: string } }>;

export type PortfolioGetResponse = BqResponse<{
  data: DashboardData<FundSingletons, FundSeries, PortfolioAsset[]>;
}>;

export type FundGetDocumentResponse = BqResponse<{
  data: GetFundDocumentResponse[];
}>;

export type GetFundDocumentResponse = {
  id: string;
  originalName: string;
  internalUrl: string;
  extractionStatus: string;
  liveness: boolean;
  restated: boolean;
  type: string;
  investorName: string;
  admissionDate: string;
  fundId: string;
  portfolioCompanyName: string;
  portfolioCompanyId: string;
  updatedAt: string;
  createdAt: Date | null;
  source: string | null;
  options?: string;
};
export type FundDocument = Omit<GetFundDocumentResponse, 'admissionDate'> & {
  admissionDate: Date | null;
};
export function isFundResponseOk(
  resp: FundGetDataResponse,
): resp is FundGetDataResponseOk {
  if (resp?.success === 1 && resp.response === 200) {
    return true;
  }
  return false;
}

export type FundDocumentPatchPayloadArgument = {
  restated?: boolean | string;
  liveness?: boolean | string;
  portfolioCompanyId?: string | null;
};

export type FundGoogleWatchAddSyncPayload = {
  folderIds: string[];
  fundId: string;
};

type FundGoogleWatchAddSyncResponse = BqResponse<{ data: object }>;

export type FundGoogleWatchDeleteSyncPayload = {
  channelId: string;
};

type FundGoogleWatchAutoSyncPayload = {
  pcGDriveFolderSync: boolean;
};

type FundGoogleWatchAutoSyncResponse = BqResponse<{ data: object }>;
